import React, { useState, useEffect } from 'react';
import { TableLoader } from '../../../components/Common/Loader';
import { Plus, Search, X, Check, ArrowLeft } from 'lucide-react';
import { getDeposAPI, createDepoAPI, updateDepoAPI } from '../../../api/locations';
import CSVButton from '../../../components/Common/CSVButton';
import { exportToCSV } from '../../../utils/csvExport';

export interface DepoZone {
  id: number;
  name: string;
}

export interface DepoItem {
  id: number;
  zoneId?: number;
  cityId?: number;
  name: string;
  sapCode?: string | null;
  sapName?: string | null;
  status?: number | string;
  zone?: DepoZone;
}

const initialDeposMock: DepoItem[] = [
  { id: 4, zoneId: 1, name: 'ANAND AGRO-PACKING STATION VASHI', sapCode: '1512', sapName: 'ANAND AGRO-PACKING STATION VASHI', zone: { id: 1, name: 'WEST' } },
  { id: 6, zoneId: 2, name: 'BANGLORE DEPOT MILK', sapCode: '1401', sapName: 'BANGLORE DEPOT MILK', zone: { id: 2, name: 'SOUTH' } },
  { id: 7, zoneId: 2, name: 'CHENNAI DEPOT MILK - Chennai', sapCode: '1402', sapName: 'CHENNAI DEPOT MILK - Chennai', zone: { id: 2, name: 'SOUTH' } },
  { id: 2, zoneId: 1, name: 'GAUGANGA NAGPUR', sapCode: '1515', sapName: 'GAUGANGA NAGPUR', zone: { id: 1, name: 'WEST' } },
];

export const DeposList: React.FC = () => {
  const [depos, setDepos] = useState<DepoItem[]>(initialDeposMock);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDepo, setEditingDepo] = useState<DepoItem | null>(null);

  // Form State
  const [name, setName] = useState('');
  const [sapCode, setSapCode] = useState('');
  const [sapName, setSapName] = useState('');
  const [zoneId, setZoneId] = useState<number>(1);

  const fetchDepos = async () => {
    try {
      setLoading(true);
      const res = await getDeposAPI();
      const rawList = res?.data || res?.depos || (Array.isArray(res) ? res : null);
      if (Array.isArray(rawList)) {
        setDepos(rawList);
      }
    } catch (err) {
      console.warn('Using fallback data for depos:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepos();
  }, []);

  const handleOpenAddModal = () => {
    setEditingDepo(null);
    setName('');
    setSapCode('');
    setSapName('');
    setZoneId(1);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (depo: DepoItem) => {
    setEditingDepo(depo);
    setName(depo.name);
    setSapCode(depo.sapCode || '');
    setSapName(depo.sapName || '');
    setZoneId(depo.zoneId || 1);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      if (editingDepo) {
        await updateDepoAPI(editingDepo.id, {
          name,
          sapCode: sapCode.trim() || undefined,
          sapName: sapName.trim() || undefined,
          zoneId,
        });
      } else {
        await createDepoAPI({
          name,
          sapCode: sapCode.trim() || undefined,
          sapName: sapName.trim() || undefined,
          zoneId,
        });
      }
      await fetchDepos();
      setIsModalOpen(false);
    } catch (err: any) {
      console.error('Error saving depo:', err);
      alert(err?.message || 'Failed to save depo');
    }
  };

  const filteredDepos = depos.filter((d) =>
    d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (d.sapCode && d.sapCode.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (d.sapName && d.sapName.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (d.zone && d.zone.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleExportCSV = () => {
    const headers = ['ID', 'Depo Name', 'SAP Code', 'SAP Name', 'Zone', 'Status'];
    const rows = filteredDepos.map((d) => [
      d.id,
      d.name,
      d.sapCode || '',
      d.sapName || '',
      d.zone?.name || '',
      d.status === 1 || d.status === '1' || d.status === 'Active' ? 'Active' : 'Inactive',
    ]);
    exportToCSV('depos_list', headers, rows);
  };

  return (
    <div className="flex flex-col gap-6 pb-6 select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Depos Management
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenAddModal}
            className="px-4 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white text-xs font-bold rounded-xl shadow-md shadow-[#C1272D]/20 flex items-center gap-2 cursor-pointer transition-all active:scale-95"
          >
            <Plus className="w-4 h-4" />
            <span>Add Depo</span>
          </button>
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-3xl border border-[#ECECF2] shadow-sm overflow-hidden flex flex-col">
        {/* Controls Bar */}
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h3 className="text-base font-bold text-slate-800">All Depos</h3>

          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
            <input
              type="text"
              placeholder="Search depos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 focus:outline-none focus:border-[#C1272D]"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFFBEB] border-b border-amber-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                <th className="py-3.5 px-6">ID</th>
                <th className="py-3.5 px-6">Depo Name</th>
                <th className="py-3.5 px-6">SAP Code</th>
                <th className="py-3.5 px-6">SAP Name</th>
                <th className="py-3.5 px-6">Zone</th>
                <th className="py-3.5 px-6 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
              {filteredDepos.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-4 px-6 font-medium text-slate-400">{d.id}</td>
                  <td className="py-4 px-6 font-bold text-slate-800">{d.name}</td>
                  <td className="py-4 px-6 font-semibold text-slate-600">{d.sapCode || 'N/A'}</td>
                  <td className="py-4 px-6 font-medium text-slate-500">{d.sapName || 'N/A'}</td>
                  <td className="py-4 px-6 font-semibold text-slate-700">
                    <span className="px-2.5 py-1 bg-amber-100/70 text-amber-800 rounded-md text-[11px] font-bold">
                      {d.zone?.name || 'WEST'}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => handleOpenEditModal(d)}
                        className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg"
                        title="View"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                      </button>
                      <button
                        onClick={() => handleOpenEditModal(d)}
                        className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {loading ? (
                <TableLoader colSpan={6} text="Loading..." />
              ) : filteredDepos.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400 font-medium">
                    No depos found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 animate-fadeIn space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <h3 className="text-lg font-extrabold text-slate-800">
                {editingDepo ? 'Edit Depo' : 'Add New Depo'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              <div className="space-y-1">
                <label className="font-bold text-slate-700">Depo Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. BANGLORE DEPOT MILK"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 focus:outline-none focus:border-[#C1272D]"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="font-bold text-slate-700">SAP Code</label>
                  <input
                    type="text"
                    placeholder="e.g. 1401"
                    value={sapCode}
                    onChange={(e) => setSapCode(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 focus:outline-none focus:border-[#C1272D]"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-slate-700">SAP Name</label>
                  <input
                    type="text"
                    placeholder="e.g. BANGLORE DEPOT MILK"
                    value={sapName}
                    onChange={(e) => setSapName(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 focus:outline-none focus:border-[#C1272D]"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-slate-700">Zone ID</label>
                <input
                  type="number"
                  value={zoneId}
                  onChange={(e) => setZoneId(Number(e.target.value))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 focus:outline-none focus:border-[#C1272D]"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white font-bold rounded-xl flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>{editingDepo ? 'Save Changes' : 'Add Depo'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeposList;
